import { useEffect, useRef, useState, useCallback } from "react";
import { useDesignMode, SelectedDomInfo } from "@/hooks/useDesignMode";
import { isEditorUIElement } from "./designUtils";

/**
 * Whitelist-based content selector.
 * Only real site content elements are selectable — never admin UI, scripts, styles, etc.
 */

const CONTENT_SELECTORS = [
  "h1", "h2", "h3", "h4", "h5", "h6",
  "p", "span", "a", "li", "label",
  "button",
  "img",
  "section", "article", "main",
  "div[class*='card']", "div[class*='hero']", "div[class*='banner']",
];

// Elements to NEVER select
const EXCLUDED_SELECTORS = [
  "[data-design-toolbar]",
  "[data-design-panel]",
  "[data-design-selector]",
  "[data-overlay-id]",
  "[data-editor-ui]",
  "[data-radix-popper-content-wrapper]",
  "[data-radix-select-viewport]",
  "[data-radix-collection-item]",
  "[data-radix-select-content]",
  "[data-radix-menu-content]",
  "[data-radix-popover-content]",
  "[role='listbox']",
  "[role='option']",
  "[role='dialog']",
  "[role='menu']",
  "[role='menuitem']",
  ".toaster",
  "nav",
  "header",
  "footer",
  "script",
  "style",
  "link",
  "meta",
  "head",
  "input",
  "textarea",
  "select",
  "[class*='Toaster']",
  "[class*='sonner']",
];

function isContentElement(el: HTMLElement): boolean {
  if (el.offsetWidth === 0 && el.offsetHeight === 0) return false;
  
  // Quick check: if it's inside any editor UI or Radix portal, reject
  if (isEditorUIElement(el)) return false;

  const tag = el.tagName.toLowerCase();
  
  for (const sel of EXCLUDED_SELECTORS) {
    try {
      if (el.matches(sel) || el.closest(sel)) return false;
    } catch { /* ignore */ }
  }

  const isWhitelisted = CONTENT_SELECTORS.some(sel => {
    try { return el.matches(sel); } catch { return false; }
  });

  if (!isWhitelisted) return false;

  if (["span", "label", "li"].includes(tag)) {
    const directText = Array.from(el.childNodes)
      .filter(n => n.nodeType === Node.TEXT_NODE)
      .map(n => n.textContent?.trim())
      .join("");
    if (!directText) return false;
  }

  return true;
}

function getComputedInfo(el: HTMLElement) {
  const cs = window.getComputedStyle(el);
  return {
    color: cs.color,
    backgroundColor: cs.backgroundColor,
    fontSize: cs.fontSize,
    fontFamily: cs.fontFamily,
    textAlign: cs.textAlign,
    borderRadius: cs.borderRadius,
    opacity: cs.opacity,
  };
}

function getCssPath(el: HTMLElement): string {
  const parts: string[] = [];
  let current: HTMLElement | null = el;
  while (current && current !== document.body) {
    let selector = current.tagName.toLowerCase();
    if (current.id) {
      selector += `#${current.id}`;
      parts.unshift(selector);
      break;
    }
    const parent = current.parentElement;
    if (parent) {
      const siblings = Array.from(parent.children).filter(
        c => c.tagName === current!.tagName
      );
      if (siblings.length > 1) {
        const idx = siblings.indexOf(current) + 1;
        selector += `:nth-of-type(${idx})`;
      }
    }
    parts.unshift(selector);
    current = current.parentElement;
  }
  return parts.join(" > ");
}

const DesignContentSelector = () => {
  const { isDesignMode, selectedDom, setSelectedDom, selectedElementId } = useDesignMode();
  const [hovered, setHovered] = useState<{ rect: DOMRect; tag: string } | null>(null);
  const highlightRef = useRef<HTMLDivElement>(null);

  // Hover highlight
  useEffect(() => {
    if (!isDesignMode) {
      setHovered(null);
      return;
    }

    const handleMouseMove = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target || isEditorUIElement(target)) {
        setHovered(null);
        return;
      }

      let el: HTMLElement | null = target;
      while (el && el !== document.body) {
        if (isContentElement(el)) {
          setHovered({ rect: el.getBoundingClientRect(), tag: el.tagName.toLowerCase() });
          return;
        }
        el = el.parentElement;
      }
      setHovered(null);
    };

    window.addEventListener("mousemove", handleMouseMove, true);
    return () => window.removeEventListener("mousemove", handleMouseMove, true);
  }, [isDesignMode]);

  // Click to select
  useEffect(() => {
    if (!isDesignMode) return;

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      
      // Ignore ALL clicks on editor UI and Radix portals
      if (isEditorUIElement(target)) return;

      // Find content element
      let el: HTMLElement | null = target;
      while (el && el !== document.body) {
        if (isContentElement(el)) {
          e.preventDefault();
          e.stopPropagation();

          const info: SelectedDomInfo = {
            element: el,
            selector: getCssPath(el),
            tagName: el.tagName.toLowerCase(),
            rect: el.getBoundingClientRect(),
            text: el.textContent?.trim().substring(0, 200) || "",
            computedStyle: getComputedInfo(el),
          };
          setSelectedDom(info);
          return;
        }
        el = el.parentElement;
      }

      // Click on empty space -> deselect
      setSelectedDom(null);
    };

    // Also block pointerdown from deselecting on Radix interactions
    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement;
      if (isEditorUIElement(target)) return;
    };

    window.addEventListener("click", handleClick, true);
    window.addEventListener("pointerdown", handlePointerDown, true);
    return () => {
      window.removeEventListener("click", handleClick, true);
      window.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [isDesignMode, setSelectedDom]);

  // Update selected rect on scroll/resize
  useEffect(() => {
    if (!isDesignMode || !selectedDom) return;
    const update = () => {
      if (selectedDom.element.isConnected) {
        setSelectedDom({
          ...selectedDom,
          rect: selectedDom.element.getBoundingClientRect(),
        });
      }
    };
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [isDesignMode, selectedDom, setSelectedDom]);

  if (!isDesignMode) return null;

  return (
    <div data-design-selector style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 8999 }}>
      {/* Hover highlight */}
      {hovered && !selectedDom && !selectedElementId && (
        <div
          style={{
            position: "fixed",
            left: hovered.rect.left - 1,
            top: hovered.rect.top - 1,
            width: hovered.rect.width + 2,
            height: hovered.rect.height + 2,
            border: "2px dashed hsl(var(--primary) / 0.5)",
            borderRadius: 4,
            pointerEvents: "none",
            transition: "all 0.1s ease",
          }}
        >
          <span
            style={{
              position: "absolute",
              top: -20,
              left: 0,
              fontSize: 10,
              background: "hsl(var(--primary))",
              color: "hsl(var(--primary-foreground))",
              padding: "1px 6px",
              borderRadius: 4,
              whiteSpace: "nowrap",
            }}
          >
            {hovered.tag}
          </span>
        </div>
      )}

      {/* Selection highlight */}
      {selectedDom && (
        <div
          style={{
            position: "fixed",
            left: selectedDom.rect.left - 2,
            top: selectedDom.rect.top - 2,
            width: selectedDom.rect.width + 4,
            height: selectedDom.rect.height + 4,
            border: "2px solid hsl(var(--primary))",
            borderRadius: 4,
            pointerEvents: "none",
            boxShadow: "0 0 0 4px hsl(var(--primary) / 0.15)",
          }}
        >
          <span
            style={{
              position: "absolute",
              top: -22,
              left: 0,
              fontSize: 10,
              background: "hsl(var(--primary))",
              color: "hsl(var(--primary-foreground))",
              padding: "2px 8px",
              borderRadius: 4,
              whiteSpace: "nowrap",
              fontWeight: 600,
            }}
          >
            {selectedDom.tagName}
          </span>
        </div>
      )}
    </div>
  );
};

export default DesignContentSelector;
